import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useDispatch } from 'react-redux';
import { motion } from 'framer-motion';
import Icon from '../ui/Icon';
import { filesActions } from '../../store';

const DropZone = ({ accept, maxFiles = 20, compact = false, onFilesAdded }) => {
  const dispatch = useDispatch();

  const onDrop = useCallback((acceptedFiles) => {
    const fileData = acceptedFiles.map(file => ({
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
      file,
    }));
    dispatch(filesActions.addFiles(fileData));
    onFilesAdded?.(fileData);
  }, [dispatch, onFilesAdded]);

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    accept,
    maxFiles,
  });

  if (compact) {
    return (
      <div {...getRootProps()} className={`drop-zone !p-6 cursor-pointer ${isDragActive ? 'active' : ''}`}>
        <input {...getInputProps()} />
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center"
            style={{ background: 'rgba(51,116,255,0.1)' }}>
            <Icon name="Upload" size={22} style={{ color: '#3374ff' }} />
          </div>
          <div className="text-left">
            <p className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>
              {isDragActive ? 'Drop files here' : 'Drop files or click to browse'}
            </p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
              PDF, Word, Excel, Images — up to 100MB
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      whileHover={{ scale: 1.005 }}
      {...getRootProps()}
      className={`drop-zone cursor-pointer ${isDragActive ? 'active' : ''} ${isDragReject ? '!border-red-400' : ''}`}
      style={{ animation: !isDragActive ? 'breathe 3.5s ease-in-out infinite' : 'none' }}
    >
      <input {...getInputProps()} />
      
      {/* Animated background circles */}
      <div className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none">
        <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full opacity-[0.03]"
          style={{ background: '#3374ff' }} />
        <div className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full opacity-[0.03]"
          style={{ background: '#8b5cf6' }} />
      </div>

      <div className="relative z-10">
        {isDragActive ? (
          <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} className="py-4">
            <div className="w-20 h-20 rounded-2xl mx-auto mb-4 flex items-center justify-center animate-bounce-soft"
              style={{ background: 'rgba(51,116,255,0.1)' }}>
              <Icon name="Download" size={36} style={{ color: '#3374ff' }} />
            </div>
            <p className="text-xl font-bold" style={{ color: '#3374ff' }}>
              Release to upload!
            </p>
          </motion.div>
        ) : (
          <>
            <div className="flex justify-center gap-3 mb-6">
              {['📄', '🖼️', '📊', '📝'].map((emoji, i) => (
                <motion.div
                  key={i}
                  animate={{ y: [0, -8, 0] }}
                  transition={{ duration: 2, delay: i * 0.3, repeat: Infinity, ease: 'easeInOut' }}
                  className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl"
                  style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-light)' }}
                >
                  {emoji}
                </motion.div>
              ))}
            </div>
            <p className="text-xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
              Drag & drop your files here
            </p>
            <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>
              or <span className="font-semibold underline" style={{ color: '#3374ff' }}>click to browse</span> your computer
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              {['PDF', 'DOCX', 'XLSX', 'PPTX', 'JPG', 'PNG'].map(fmt => (
                <span key={fmt} className="px-3 py-1 rounded-full text-xs font-medium"
                  style={{ background: 'var(--bg-tertiary)', color: 'var(--text-muted)' }}>
                  .{fmt.toLowerCase()}
                </span>
              ))}
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
};

export default DropZone;
